/**
 * Centralized Product Pricing System
 * Handles all price calculations including:
 * - Base product prices
 * - Quantity changes
 * - Avis Plus add-ons
 * - Cross-selling products
 * - Volume discounts
 */

class ProductPricing {
  constructor() {
    this.basePrice = 0;
    this.currentQuantity = 1;
    this.addonPrice = 0;
    this.crossSellPrice = 0;
    this.volumeDiscount = 0;
    this.priceElements = new Map();
    this.observers = new Map();

    this.init();
  }

  init() {
    this.setupPriceElements();
    this.setupEventListeners();
    this.setupMutationObservers();
    this.setupPriceWatchdog();
    this.updateAllPrices();
  }

  /**
   * Find and register all price display elements
   */
  setupPriceElements() {
    // Main product price elements
    const mainPriceElement = document.getElementById('total-price');
    if (mainPriceElement) {
      this.basePrice = parseFloat(mainPriceElement.dataset.soloPrice) || 0;
      this.priceElements.set('main', mainPriceElement);
    }

    // Cross-sell price elements
    document.querySelectorAll('[id^="crosssell-price-"]').forEach((element) => {
      const variantId = element.id.replace('crosssell-price-', '');
      this.priceElements.set(`crosssell-${variantId}`, element);
    });

    // Volume discount elements
    const volumeDiscountElement = document.getElementById('volume-discount-price');
    if (volumeDiscountElement) {
      this.priceElements.set('volume-discount', volumeDiscountElement);
    }
  }

  /**
   * Setup event listeners for quantity changes and add-on updates
   */
  setupEventListeners() {
    // Main quantity controls
    const quantityInput = document.getElementById('quantity-input');
    const decreaseBtn = document.getElementById('quantity-decrease');
    const increaseBtn = document.getElementById('quantity-increase');

    if (quantityInput) {
      quantityInput.addEventListener('change', (e) => this.updateQuantity(parseInt(e.target.value) || 1));
      quantityInput.addEventListener('input', (e) => this.handleQuantityInput(e));
      quantityInput.addEventListener('focus', (e) => e.target.select());
      quantityInput.addEventListener('blur', (e) => this.updateQuantity(parseInt(e.target.value) || 1));
    }

    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', () => this.updateQuantity(this.currentQuantity - 1));
    }

    if (increaseBtn) {
      increaseBtn.addEventListener('click', () => this.updateQuantity(this.currentQuantity + 1));
    }

    // Setup cross-sell controls with retry mechanism
    this.setupCrossSellControls();

    // Avis Plus add-on changes
    document.addEventListener('avis-option-changed', () => this.updateAddonPrice());
    document.addEventListener('avis-price-updated', () => this.updateAddonPrice());

    // PO (Optis/BSS) add-on changes – when user checks/unchecks e.g. "Bedrukken"
    const productContainer = document.getElementById('product-container');
    if (productContainer) {
      productContainer.addEventListener('change', (e) => {
        if (e.target.matches && e.target.matches('.po-option--container input, .po-label-container input, [class*="po-option"] input')) {
          this.updateAddonPrice();
        }
      });
    }

    // Variant change: sync base price, re-read add-on (so it stays in total), then recalculate
    document.addEventListener('variant:changed', (e) => {
      if (e.detail && typeof e.detail.price === 'number') {
        this.basePrice = e.detail.price;
        this.updateAddonPrice(); // Re-read add-on so total = base + add-on after variant switch
      }
    });

    // Custom events for price updates
    // NOTE: 'product:quantity-changed' is dispatched BY updateQuantity() itself (below).
    // A prior version of this code also listened for that same event here and called
    // updateQuantity() again, which re-dispatched the event, causing synchronous infinite
    // recursion (RangeError: Maximum call stack size exceeded) on every +/- click. Nothing
    // legitimately needs to react to this event by re-invoking updateQuantity - it is already
    // called directly by the quantity button/input handlers above - so that listener has been
    // removed rather than re-added here.
    // Removed product:addon-changed listener to prevent circular dependency
    document.addEventListener('product:crosssell-changed', (e) => this.updateCrossSellPrice());
  }

  /**
   * Setup cross-sell controls - exactly like the working version
   */
  setupCrossSellControls() {
    // Handle cross-sell quantity changes - exactly as it was working before
    document.querySelectorAll('.crosssell-increase').forEach((button) => {
      button.addEventListener('click', function () {
        const variantId = this.dataset.variantId;
        const input = document.getElementById(`crosssell-quantity-${variantId}`);
        const currentValue = parseInt(input.value) || 0;
        input.value = currentValue + 1;

        // Update main product price if cross-sell affects total
        if (window.productPricing) {
          window.productPricing.updateCrossSellPrice();
        }
      });
    });

    document.querySelectorAll('.crosssell-decrease').forEach((button) => {
      button.addEventListener('click', function () {
        const variantId = this.dataset.variantId;
        const input = document.getElementById(`crosssell-quantity-${variantId}`);
        const currentValue = parseInt(input.value) || 0;
        if (currentValue > 0) {
          input.value = currentValue - 1;
        }

        // Update main product price if cross-sell affects total
        if (window.productPricing) {
          window.productPricing.updateCrossSellPrice();
        }
      });
    });

    // Handle direct input changes
    document.querySelectorAll('[id^="crosssell-quantity-"]').forEach((input) => {
      input.addEventListener('change', function () {
        const value = parseInt(this.value) || 0;
        this.value = Math.max(0, value);

        // Update main product price if cross-sell affects total
        if (window.productPricing) {
          window.productPricing.updateCrossSellPrice();
        }
      });

      input.addEventListener('focus', function () {
        this.select();
      });
    });
  }

  /**
   * Setup mutation observers for dynamic content changes
   */
  setupMutationObservers() {
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          const target = mutation.target;
          if (
            target.classList.contains('apo-total-addcharge') ||
            target.closest('[class*="avis"], [data-avis]') ||
            target.classList.contains('avis') ||
            target.hasAttribute('data-avis') ||
            target.closest('.apo-total-addcharge') ||
            target.closest('.po-option--container, .po-label-container, [class*="po-option"]')
          ) {
            shouldUpdate = true;
          }
        }
      });

      if (shouldUpdate) {
        setTimeout(() => this.updateAddonPrice(), 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-avis', 'value'],
    });

    this.observers.set('avis', observer);
  }

  /**
   * Watch #total-price itself for external overwrites and self-heal.
   *
   * Root cause of the "Incl. BTW €X shows the excl.-BTW number and never corrects itself"
   * bug: some time after this class's own correct (VAT-aware) initial paint, a third-party
   * app re-renders the price markup (observed ~3-5s after load, via the BSS/Optis
   * product-options app's own DOM updates) using the raw, excl.-BTW value that Shopify's
   * Liquid renders server-side - #total-price's ORIGINAL static content before any JS ever
   * touches it. This is a ONE-TIME late overwrite, not a continuous race, so it happens at
   * an unpredictable moment that no fixed setTimeout delay can reliably beat.
   *
   * This watchdog observes #total-price directly and, whenever its text stops matching what
   * formatPrice() currently computes from our own known-good state (basePrice/quantity/
   * addonPrice/volumeDiscount), immediately repaints it correctly - regardless of who or
   * what caused the change, and regardless of when. The reentrancy guard prevents our own
   * corrective write from re-triggering this same observer.
   */
  setupPriceWatchdog() {
    const mainPriceElement = this.priceElements.get('main');
    if (!mainPriceElement) return;

    let selfWriting = false;
    const reconcile = () => {
      if (selfWriting) return;
      const totalPrice =
        this.basePrice * this.currentQuantity + this.addonPrice * this.currentQuantity - this.volumeDiscount;
      const expected = this.formatPrice(totalPrice);
      if (mainPriceElement.textContent.trim() !== expected) {
        selfWriting = true;
        mainPriceElement.textContent = expected;
        mainPriceElement.dataset.price = totalPrice;
        selfWriting = false;
      }
    };

    const watchdog = new MutationObserver(() => reconcile());
    watchdog.observe(mainPriceElement, { childList: true, characterData: true, subtree: true });
    this.observers.set('price-watchdog', watchdog);
  }

  /**
   * Update main product quantity
   */
  updateQuantity(newQuantity) {
    if (newQuantity < 1) return;

    this.currentQuantity = newQuantity;

    // Update quantity input
    const quantityInput = document.getElementById('quantity-input');
    const hiddenQuantityInput = document.getElementById('hidden-quantity');

    if (quantityInput) quantityInput.value = this.currentQuantity;
    if (hiddenQuantityInput) hiddenQuantityInput.value = this.currentQuantity;

    this.updateAllPrices();

    // Dispatch custom event
    document.dispatchEvent(
      new CustomEvent('product:quantity-changed', {
        detail: { quantity: this.currentQuantity },
      })
    );
  }

  /**
   * Handle quantity input changes with validation
   */
  handleQuantityInput(event) {
    if (event.target.value === '') return;

    const newQuantity = parseInt(event.target.value);
    if (newQuantity && newQuantity >= 1) {
      this.currentQuantity = newQuantity;
      const hiddenQuantityInput = document.getElementById('hidden-quantity');
      if (hiddenQuantityInput) hiddenQuantityInput.value = this.currentQuantity;
      this.updateAllPrices();
    }
  }

  /**
   * Update cross-sell product quantity
   */
  updateCrossSellQuantity(variantId, change, absoluteValue = null) {
    const input = document.getElementById(`crosssell-quantity-${variantId}`);
    if (!input) return;

    let newQuantity;
    if (absoluteValue !== null) {
      newQuantity = Math.max(0, absoluteValue);
    } else {
      const currentValue = parseInt(input.value) || 0;
      newQuantity = Math.max(0, currentValue + change);
    }

    input.value = newQuantity;
    this.updateCrossSellPrice();

    // Dispatch custom event
    document.dispatchEvent(
      new CustomEvent('product:crosssell-changed', {
        detail: { variantId, quantity: newQuantity },
      })
    );
  }

  /**
   * Parse PO (Optis/BSS) extra price string to cents, e.g. "(+ €4,99)" or "+ €4,99" -> 499
   */
  parsePoExtraPriceCents(text) {
    if (!text || typeof text !== 'string') return 0;
    const match = text.replace(/\s/g, '').match(/[€]?\s*([\d.,]+)/);
    if (!match) return 0;
    const value = parseFloat(match[1].replace(',', '.')) || 0;
    return Math.round(value * 100);
  }

  /**
   * Sum add-on price from PO (Optis/BSS) selected options (.po-extra-price on checked options)
   */
  getPoAddonPriceCents() {
    let total = 0;
    const container = document.getElementById('product-container') || document;
    container.querySelectorAll('.po-option--container input:checked, .po-label-container input:checked, [class*="po-option"] input:checked').forEach((input) => {
      const wrapper = input.closest('.po-option--checkbox-wrapper, .po-option--container, .po-label, [class*="po-option"]');
      const extraPriceEl = wrapper && wrapper.querySelector('.po-extra-price');
      if (extraPriceEl && extraPriceEl.textContent) {
        total += this.parsePoExtraPriceCents(extraPriceEl.textContent);
      }
    });
    return total;
  }

  /**
   * Update add-on price from Avis Plus and PO (Optis/BSS) product options
   */
  updateAddonPrice() {
    let totalAddon = 0;

    // Avis Plus
    const avisTotalAddcharge = document.querySelector('.apo-total-addcharge');
    if (avisTotalAddcharge && avisTotalAddcharge.value) {
      totalAddon += parseFloat(avisTotalAddcharge.value) || 0;
    }

    // PO (Optis/BSS) – e.g. "Personaliseer" / "Bedrukken" checkboxes with .po-extra-price
    totalAddon += this.getPoAddonPriceCents();

    this.addonPrice = totalAddon;
    this.updateAllPrices();
  }

  /**
   * Calculate cross-sell total price
   * Note: Cross-sell prices are NOT included in the main product price display
   * They are only calculated for cart purposes, not for PDP display
   */
  updateCrossSellPrice() {
    this.crossSellPrice = 0;

    document.querySelectorAll('[id^="crosssell-quantity-"]').forEach((input) => {
      const variantId = input.id.replace('crosssell-quantity-', '');
      const quantity = parseInt(input.value) || 0;
      const priceElement = this.priceElements.get(`crosssell-${variantId}`);

      if (priceElement && quantity > 0) {
        const price = parseFloat(priceElement.dataset.price) || 0;
        this.crossSellPrice += price * quantity;
      }
    });

    // Don't update main price - cross-sell doesn't affect PDP price display
    // Only update if needed for other purposes (like cart calculation)
    // this.updateAllPrices(); // Removed - cross-sell shouldn't trigger main price update
  }

  /**
   * Calculate volume discount (if applicable)
   * Note: Volume discounts are now handled by HulkDiscount app
   */
  calculateVolumeDiscount() {
    // Volume discounts are handled by HulkDiscount app
    this.volumeDiscount = 0;
  }

  /**
   * Update all price displays
   */
  updateAllPrices() {
    this.calculateVolumeDiscount();

    // Calculate total price (EXCLUDING cross-sell items - they're only added in cart)
    // Cross-sell items should NOT affect the PDP price display
    const totalPrice =
      this.basePrice * this.currentQuantity + this.addonPrice * this.currentQuantity - this.volumeDiscount;

    // Update main price display (without cross-sell prices)
    const mainPriceElement = this.priceElements.get('main');
    if (mainPriceElement) {
      const formattedPrice = this.formatPrice(totalPrice);
      mainPriceElement.textContent = formattedPrice;
      mainPriceElement.dataset.price = totalPrice;
    }

    // Update volume discount display
    const volumeDiscountElement = this.priceElements.get('volume-discount');
    if (volumeDiscountElement && this.volumeDiscount > 0) {
      const discountPrice = totalPrice + this.volumeDiscount;
      const formattedDiscountPrice = this.formatPrice(discountPrice);
      volumeDiscountElement.textContent = formattedDiscountPrice;
    }

    // Dispatch price updated event
    document.dispatchEvent(
      new CustomEvent('product:price-updated', {
        detail: {
          basePrice: this.basePrice,
          quantity: this.currentQuantity,
          addonPrice: this.addonPrice,
          crossSellPrice: this.crossSellPrice,
          volumeDiscount: this.volumeDiscount,
          totalPrice: totalPrice,
        },
      })
    );
  }

  /**
   * Format price for display
   * VAT-aware: converts the raw excl-BTW cents to the currently selected display mode
   * (incl./excl. BTW) via the global window.HVVat engine (see layout/theme.liquid).
   * Without this, updateAllPrices() re-painted #total-price with the raw excl-BTW value
   * any time it ran (e.g. from this class's own MutationObserver reacting to unrelated
   * DOM churn from the BSS/Optis product-options app during page load, or from quantity/
   * add-on changes) - stomping over the correct VAT-aware price written elsewhere and
   * leaving the "Incl. BTW" label mismatched against an excl.-looking number that never
   * self-corrects. Fail safe if HVVat isn't available yet (script load order) - fall back
   * to raw cents rather than throwing, so the page never breaks.
   */
  formatPrice(price) {
    let displayCents = price;
    try {
      if (window.HVVat && typeof window.HVVat.apply === 'function') {
        displayCents = window.HVVat.apply(price);
      }
    } catch (e) {
      displayCents = price;
    }
    const formattedPrice = (displayCents / 100).toFixed(2).replace('.', ',');
    return `€${formattedPrice}`;
  }

  /**
   * Get current pricing data
   * Note: totalPrice excludes cross-sell items (they're only added in cart)
   */
  getPricingData() {
    return {
      basePrice: this.basePrice,
      quantity: this.currentQuantity,
      addonPrice: this.addonPrice,
      crossSellPrice: this.crossSellPrice, // Calculated but not included in displayed total
      volumeDiscount: this.volumeDiscount,
      totalPrice: this.basePrice * this.currentQuantity + this.addonPrice * this.currentQuantity - this.volumeDiscount, // Cross-sell excluded from PDP display
    };
  }

  /**
   * Cleanup observers
   */
  destroy() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();
    this.priceElements.clear();
  }
}

// Initialize pricing system when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  // Only initialize if we're on a product page, and only if an instance doesn't already
  // exist - defense in depth against this script being included more than once on the
  // same page (e.g. via both em-pdp-crossselling.liquid and
  // em-add-to-cart-with-quantity.liquid), which would otherwise create duplicate
  // ProductPricing instances each wiring up their own set of event listeners.
  if (!window.productPricing && (document.getElementById('total-price') || document.querySelector('[id^="crosssell-quantity-"]'))) {
    window.productPricing = new ProductPricing();
    // Defensive re-check, mirroring the setTimeout(updatePrice, 500) pattern in
    // em-add-to-cart-with-quantity.liquid: on some loads, other scripts running during
    // DOMContentLoaded (product-options apps, etc.) can touch #total-price after this
    // class's own initial updateAllPrices() call, leaving a stale/raw price on screen
    // momentarily. Re-running the VAT-aware updateAllPrices() shortly after load closes
    // that early window; setupPriceWatchdog() (called from init(), above) handles any
    // LATER external overwrite (observed happening several seconds after load, well past
    // this timer) by continuously self-healing #total-price whenever it changes.
    setTimeout(function () {
      if (window.productPricing) window.productPricing.updateAllPrices();
    }, 600);
  }
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductPricing;
}
